import { Box, Button, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Slider, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import BasicModal from '../common/BasicModal.tsx';
import axios from 'axios';
import IResponse from '../../interfaces/response.ts';
import Autocomplete from '@mui/material/Autocomplete';
import InfoIcon from '@mui/icons-material/Info';
import AlertModal from '../common/AlertModal.tsx';

const pageSchema = z.object({
	title: z.string().min(3, 'invalid_min_title_message'),
	type: z.string().min(1, 'required'),
	price: z.coerce.number().min(1, 'price_required_message'),
	description: z.string().min(3, 'description_required_message'),
});

interface PriceType {
	value?: string;
	id?: string;
}
interface PriceModalProps {
	open: boolean;
	onClose: () => void;
	id: number;
	media_id?: number;
	editPrice?: number;
	openAlert: boolean;
	setOpenAlert: (open: boolean) => void;
}

interface Data {
	title: string;
	type: string;
	price: number;
	co_price: number;
	description: string;
}

const PriceModal: React.FC<PriceModalProps> = ({ open, onClose, openAlert, setOpenAlert, id, media_id, editPrice }) => {
	const { t } = useTranslation();
	const [typeList, setTypeList] = useState<PriceType[]>([]);
	const [coPercent, setCoPercent] = useState<number>(40);
	const [min, setMin] = useState<number>(0);
	const [max, setMax] = useState<number>(100);
	const [campaign_type, setCampaignType] = useState<string>('');
	const [showError, setShowError] = useState<boolean>(false);
	const handleChange = (event: SelectChangeEvent) => {
		setCampaignType(event.target.value as string);
	};
	useEffect(() => {
		if (campaign_type.length !== 0) {
			setShowError(false);
		}
	}, [campaign_type, typeList]);

	useEffect(() => {
		axios({
			url: `settings`,
			method: 'GET',
		}).then(response => {
			const data = response?.data?.data;
			setMax(data.max_percent_cooperation);
			setMin(data.min_percent_cooperation);
		});
	}, []);

	useEffect(() => {
		if (media_id) {
			axios({
				url: `admin/medias/types/${media_id}`,
				method: 'GET',
				loading: true,
			}).then(response => {
				const data = response?.data?.data;
				if (Array.isArray(data)) {
					setTypeList(data);
				} else {
					setTypeList([]);
				}
			});
		}
	}, [media_id, open]);

	useEffect(() => {
		reset();
	}, [open]);

	const valuePercent = (newValue: any) => {
		setCoPercent(newValue.target.value as number);
	};

	useEffect(() => {
		if (editPrice !== undefined && editPrice > 0) {
			axios<IResponse<Data>>({
				url: `/media_prices/${editPrice}`,
				method: 'GET',
				loading: true,
			}).then(response => {
				if (response?.data?.success) {
					setValue('title', response.data.data.title);
					setValue('type', response.data.data.type);
					setValue('price', response.data.data.price);
					setValue('description', response.data.data.description);
				}
			});
		}
	}, [editPrice, open]);

	const {
		register,
		handleSubmit,
		formState: { errors },
		control,
		watch,
		setValue,
		reset,
	} = useForm<z.infer<typeof pageSchema>>({
		resolver: zodResolver(pageSchema),
		defaultValues: {},
	});

	const handleModalSubmit = (data: z.infer<typeof pageSchema>) => {
		if (campaign_type.length !== 0) {
			if (editPrice !== undefined && editPrice > 0) {
				axios<IResponse<string>>({
					url: `/media_prices/${editPrice}`,
					method: 'PUT',
					loading: true,
					data: { ...data, media_entity_id: id, co_percent: coPercent, campaign_type: campaign_type },
				}).then(() => {
					onClose();
				});
			} else {
				axios<IResponse<string>>({
					url: `/media_prices`,
					method: 'POST',
					loading: true,
					data: { ...data, media_entity_id: id, co_percent: coPercent, campaign_type: campaign_type },
				}).then(() => {
					onClose();
				});
			}
		} else {
			setShowError(true);
		}
	};

	const priceEl = watch('price');
	const coPriceHandle = (coPercent: number) => {
		const perc = 100 - coPercent;
		return Math.round(priceEl * (perc / 100));
	};
	return (
		<>
			<div className="flex w-full flex-col">
				<AlertModal text={'text_alert_for_colleague'} id="alert-modal" open={openAlert} setOpen={setOpenAlert} />

				<BasicModal open={open} onClose={onClose} title={t('add_new_item')} showBackIcon={false}>
					<form className="flex w-full flex-col gap-6 rounded bg-background-paper px-8 pb-0.5 pt-6 text-base" onSubmit={handleSubmit(handleModalSubmit)}>
						<Box className="flex rounded-md border border-dashed border-grey-300 bg-grey-100 px-6 py-4">
							<InfoIcon className="me-2 h-6 w-6 text-grey-600" />
							<Box className="text-sm leading-7 text-text-secondary">
								<ol className="ps-2 text-base text-text-primary">{t('percent_influenceral')}</ol>
							</Box>
						</Box>
						<div className="mb-8 me-4 mt-5 flex flex-col gap-3">
							<TextField fullWidth size="small" label={t('title_tariff')} {...register('title')} error={!!errors.title} helperText={t(errors.title?.message ?? '')} InputLabelProps={{ shrink: !!watch('title') }} />
							<Controller
								control={control}
								name="type"
								rules={{
									required: t('required'),
								}}
								render={({ field, fieldState }) => (
									<Autocomplete
										size="small"
										disablePortal
										options={typeList}
										getOptionLabel={option => option?.value || ''}
										filterSelectedOptions
										isOptionEqualToValue={(option, value) => option?.id === value?.id}
										onChange={(_, selectedOption) => {
											field.onChange(selectedOption?.id || null);
										}}
										value={typeList.find(option => option.id === field.value) || null}
										renderInput={params => <TextField {...params} label={t('category')} InputLabelProps={{ shrink: !!watch('type') }} error={!!fieldState.error} helperText={fieldState.error?.message ? t(fieldState.error.message) : ''} />}
									/>
								)}
							/>

							<FormControl fullWidth>
								<InputLabel id="demo-simple-select-label">{t('campaign_type')}</InputLabel>
								<Select labelId="demo-simple-select-label" id="demo-simple-select" value={campaign_type} label="Age" onChange={handleChange}>
									<MenuItem value={'reportage'}>{t('reportage')}</MenuItem>
									<MenuItem value={'advertising'}>{t('advertising')}</MenuItem>
								</Select>
								{showError && <p className="mt-2 text-xs text-red-500">فیلد اجباری میباشد</p>}
							</FormControl>
							<TextField fullWidth size="small" label={t('price')} {...register('price')} error={!!errors.price} helperText={errors.price?.message ? t(errors.price.message) : ''} InputLabelProps={{ shrink: !!watch('price') }} />
							<Typography id="input-slider" gutterBottom>
								{t('collaboration_percent')}({coPercent})
							</Typography>
							<Typography>
								{coPriceHandle(coPercent)} {t('tooman')}
							</Typography>
							<Slider aria-label="Temperature" value={Number(coPercent)} valueLabelDisplay="auto" step={5} marks min={Number(min)} max={Number(max)} onChange={valuePercent} />
							<TextField fullWidth size="small" multiline rows={4} label={t('description')} {...register('description')} error={!!errors.description} helperText={errors.description?.message ? t(errors.description.message) : ''} InputLabelProps={{ shrink: !!watch('description') }} />
							<div className="flex flex-row-reverse gap-2">
								<Button variant="contained" disabled={showError} className="my-2 w-28" type="submit">
									{t('add')}
								</Button>
								<Button variant="outlined" className="my-2 w-20 border-grey-400 text-grey-400" onClick={onClose}>
									{t('cancel')}
								</Button>
							</div>
						</div>
					</form>
				</BasicModal>
			</div>
		</>
	);
};

export default PriceModal;
